import { IconButton, Typography } from '@material-ui/core';
import CenterFocusWeakRoundedIcon from '@material-ui/icons/CenterFocusWeakRounded';
import { FC, memo, ReactNode, useCallback, useState } from 'react';
import { WbDrawer, WbDrawerContent } from '../WbDrawer';
import { WbTextField } from '../WbTextField';
import { WbTooltip } from '../WbTooltip';
import { YamlPathViewer } from './YamlPathViewer';

export interface WbYamlPathPickerProps {
  label: string;
  value: string;
  onChange: (path: string) => void;
  /** Object shown as YAML in the picker drawer. */
  data?: unknown;
  loading?: boolean;
  error?: string;
  /** Called when the drawer opens, e.g. to lazily load `data`. */
  onOpen?: () => void;
  /** When true (default) the path can also be typed; otherwise it can only be picked. */
  free?: boolean;
  helperText?: ReactNode;
  placeholder?: string;
  disabled?: boolean;
  drawerTitle?: string;
  className?: string;
}

export const WbYamlPathPicker: FC<WbYamlPathPickerProps> = memo(
  ({
    label,
    value,
    onChange,
    data,
    loading = false,
    error,
    onOpen,
    free = true,
    helperText,
    placeholder,
    disabled = false,
    drawerTitle = 'Select a field',
    className,
  }) => {
    const [open, setOpen] = useState(false);

    const openDrawer = useCallback(() => {
      if (disabled) return;
      setOpen(true);
      onOpen?.();
    }, [disabled, onOpen]);

    const handleSelect = useCallback(
      (path: string) => {
        onChange(path);
        setOpen(false);
      },
      [onChange],
    );

    return (
      <>
        <WbTextField
          className={className}
          label={label}
          value={value}
          placeholder={placeholder}
          helperText={helperText}
          disabled={disabled}
          fullWidth
          cursorType={free ? undefined : 'pointer'}
          onChange={event => onChange(event.target.value)}
          onClick={free ? undefined : openDrawer}
          inputProps={{ readOnly: !free }}
          InputProps={{
            endAdornment: (
              <WbTooltip title="Pick from YAML">
                <span>
                  <IconButton
                    size="small"
                    edge="end"
                    aria-label={`Pick ${label} from YAML`}
                    disabled={disabled}
                    onClick={event => {
                      event.stopPropagation();
                      openDrawer();
                    }}
                  >
                    <CenterFocusWeakRoundedIcon />
                  </IconButton>
                </span>
              </WbTooltip>
            ),
          }}
        />
        <WbDrawer
          open={open}
          setOpen={setOpen}
          title={drawerTitle}
          size={50}
          ModalProps={{ keepMounted: false }}
        >
          <WbDrawerContent>
            {loading && <Typography role="status">Loading...</Typography>}
            {!loading && error && (
              <Typography role="alert" color="error">
                {error}
              </Typography>
            )}
            {!loading && !error && data !== undefined && (
              <YamlPathViewer
                data={data}
                ariaLabel={`${label} YAML`}
                onSelectPath={handleSelect}
              />
            )}
          </WbDrawerContent>
        </WbDrawer>
      </>
    );
  },
);
